import dynamic, { DynamicOptions } from 'next/dynamic';
import { ComponentType } from 'react';

type DynamicImportLoader<P> = () => Promise<{ default: ComponentType<P> } | ComponentType<P>>;

const isChunkLoadError = (error: Error): boolean => {
  return (
    error.name === 'ChunkLoadError' ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Failed to fetch dynamically imported module')
  );
};

const tryReloadPage = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const hasReloaded = sessionStorage.getItem('chunk_reload_attempted');
    if (!hasReloaded) {
      sessionStorage.setItem('chunk_reload_attempted', 'true');
      window.location.reload();
      return true;
    }
  } catch {
    // sessionStorage might be blocked (private browsing, etc.)
    // Fall through and let the error propagate
  }
  return false;
};

/**
 * A wrapper around next/dynamic that adds retry logic for chunk load failures.
 */
export function safeDynamic<P = {}>(
  loader: DynamicImportLoader<P>,
  options?: DynamicOptions<P>
): ComponentType<P> {
  const retryLoader = () =>
    loader().catch((error: Error) => {
      if (isChunkLoadError(error)) {
        const willReload = tryReloadPage();
        if (willReload) {
          return { default: (() => null) as unknown as ComponentType<P> };
        }
      }
      throw error;
    });

  return dynamic(retryLoader, {
    loading: () => null,
    ...options,
  });
}

/**
 * Retry a dynamic import with exponential backoff.
 */
export function safeDynamicWithRetry<P = {}>(
  loader: DynamicImportLoader<P>,
  options?: DynamicOptions<P> & { maxRetries?: number }
): ComponentType<P> {
  const { maxRetries = 3, ...dynamicOptions } = options || {};

  const retryLoader = async () => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await loader();
      } catch (error) {
        lastError = error as Error;

        if (!isChunkLoadError(lastError) || attempt === maxRetries - 1) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, attempt)));
      }
    }

    if (lastError && isChunkLoadError(lastError)) {
      const willReload = tryReloadPage();
      if (willReload) {
        return { default: (() => null) as unknown as ComponentType<P> };
      }
    }

    throw lastError;
  };

  return dynamic(retryLoader, {
    loading: () => null,
    ...dynamicOptions,
  });
}
